import apiClient from '../utils/apiClient';

/**
 * Branch API Services
 * Quản lý chi nhánh, QR code và IP whitelist
 */

export const branchAPI = {
  /**
   * Lấy danh sách chi nhánh với phân trang và lọc
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.search - Từ khóa tìm kiếm
   * @param {boolean} params.is_active - Lọc theo trạng thái active
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách chi nhánh và metadata phân trang
   */
  getBranches: async (params = {}) => {
    const { search = '', is_active = true, page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: '/branches',
      params: { search, is_active, page, limit },
    });
  },

  /**
   * Lấy thông tin chi tiết chi nhánh theo ID
   * @param {string} branchId - ID chi nhánh
   * @returns {Promise<Object>} Thông tin chi tiết chi nhánh
   */
  /**
   * Danh sách chi nhánh ĐANG HOẠT ĐỘNG dạng gọn (id, name) — nguồn duy nhất cho các bộ chọn
   * chi nhánh. Mở chi nhánh thứ 4 là tự xuất hiện, KHÔNG hardcode danh sách chi nhánh (#125114).
   * @returns {Promise<Array>} [{ id, name, ... }]
   */
  getActiveSimple: async () => {
    return await apiClient.get({ url: '/branches/active/simple' });
  },

  getBranchById: async (branchId) => {
    return await apiClient.get({
      url: `/branches/${branchId}`,
    });
  },

  /**
   * Lấy danh sách món của chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @returns {Promise<Object>} Danh sách món của chi nhánh với giá
   */
  getBranchMenu: async (branchId) => {
    return await apiClient.get({
      url: `/branches/${branchId}/menu`,
    });
  },

  /**
   * Tạo chi nhánh mới (ADMIN only)
   * @param {Object} branchData - Dữ liệu chi nhánh
   * @param {string} branchData.name - Tên chi nhánh
   * @param {string} branchData.code - Mã chi nhánh
   * @param {string} branchData.address - Địa chỉ
   * @param {Array} branchData.ip_whitelist - Danh sách IP whitelist
   * @param {string} branchData.qr_code_secret - Secret cho QR code
   * @param {string} branchData.manager_id - ID quản lý
   * @returns {Promise<Object>} Thông tin chi nhánh vừa tạo
   */
  createBranch: async (branchData) => {
    return await apiClient.post({
      url: '/branches',
      data: branchData,
    });
  },

  /**
   * Cập nhật thông tin chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {Object} updateData - Dữ liệu cập nhật
   * @returns {Promise<Object>} Thông tin chi nhánh sau khi cập nhật
   */
  updateBranch: async (branchId, updateData) => {
    return await apiClient.put({
      url: `/branches/${branchId}`,
      data: updateData,
    });
  },

  /**
   * Xóa chi nhánh (soft delete)
   * @param {string} branchId - ID chi nhánh
   * @returns {Promise<Object>} Kết quả xóa
   */
  deleteBranch: async (branchId) => {
    return await apiClient.delete({
      url: `/branches/${branchId}`,
    });
  },

  /**
   * Lấy thống kê chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @returns {Promise<Object>} Thống kê chi nhánh
   */
  getBranchStats: async (branchId) => {
    return await apiClient.get({
      url: `/branches/${branchId}/stats`,
    });
  },

  /**
   * Lấy QR code cho chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @returns {Promise<Object>} QR code string
   */
  getBranchQRCode: async (branchId) => {
    return await apiClient.get({
      url: `/branches/${branchId}/qr-code`,
    });
  },

  /**
   * Validate IP address với whitelist của chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {string} ipAddress - IP address cần validate
   * @returns {Promise<Object>} Kết quả validation
   */
  validateIP: async (branchId, ipAddress) => {
    return await apiClient.post({
      url: `/branches/${branchId}/validate-ip`,
      data: { ip_address: ipAddress },
    });
  },

  /**
   * Cập nhật IP whitelist cho chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {Array} ipWhitelist - Danh sách IP whitelist mới
   * @returns {Promise<Object>} Kết quả cập nhật
   */
  updateIPWhitelist: async (branchId, ipWhitelist) => {
    // Backend dùng PUT /branches/:id với body partial
    return await apiClient.put({
      url: `/branches/${branchId}`,
      data: { ip_whitelist: ipWhitelist },
    });
  },

  /**
   * Cập nhật QR code secret cho chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {string} secret - Secret mới cho QR code
   * @returns {Promise<Object>} Kết quả cập nhật
   */
  updateQRSecret: async (branchId, secret) => {
    // Backend dùng PUT /branches/:id với body partial
    return await apiClient.put({
      url: `/branches/${branchId}`,
      data: { qr_code_secret: secret },
    });
  },

  /**
   * Lấy danh sách nhân viên của chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {Object} params - Tham số truy vấn
   * @returns {Promise<Object>} Danh sách nhân viên
   */
  getBranchEmployees: async (branchId, params = {}) => {
    const { search = '', page = 1, limit = 10 } = params;
    return await apiClient.get({
      url: `/branches/${branchId}/employees`,
      params: { search, page, limit },
    });
  },

  /**
   * Thêm món vào menu của chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {string} menuItemId - ID món ăn
   * @param {number} priceOverride - Giá riêng cho chi nhánh (optional)
   * @returns {Promise<Object>} Kết quả thêm món
   */
  addMenuItemToBranch: async (branchId, menuItemId, priceOverride = null) => {
    return await apiClient.post({
      url: `/branches/${branchId}/menu-items`,
      data: {
        menuItemId: menuItemId,
        priceOverride: priceOverride,
      },
    });
  },

  /**
   * Xóa món khỏi menu của chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {string} menuItemId - ID món ăn
   * @returns {Promise<Object>} Kết quả xóa món
   */
  removeMenuItemFromBranch: async (branchId, menuItemId) => {
    return await apiClient.delete({
      url: `/branches/${branchId}/menu-items/${menuItemId}`,
    });
  },

  /**
   * Thêm nhiều món vào menu của chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {Array<string>} menuItemIds - Danh sách ID món ăn
   * @returns {Promise<Object>} Kết quả thêm món
   */
  addMenuItemsBatch: async (branchId, menuItemIds) => {
    return await apiClient.post({
      url: `/branches/${branchId}/menu-items/batch`,
      data: {
        menuItemIds: menuItemIds,
      },
    });
  },

  /**
   * Xóa tất cả món khỏi menu của chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @returns {Promise<Object>} Kết quả xóa
   */
  clearBranchMenu: async (branchId) => {
    return await apiClient.delete({
      url: `/branches/${branchId}/menu-items`,
    });
  },

  /**
   * Sao chép menu từ chi nhánh này sang chi nhánh khác
   * @param {string} fromBranchId - ID chi nhánh nguồn
   * @param {string} toBranchId - ID chi nhánh đích
   * @param {string} adjustmentType - Loại điều chỉnh: 'exact' | 'percentage' | 'amount'
   * @param {number} adjustmentValue - Giá trị điều chỉnh
   * @returns {Promise<Object>} Kết quả sao chép
   */
  copyBranchMenu: async (fromBranchId, toBranchId, adjustmentType = 'exact', adjustmentValue = 0) => {
    return await apiClient.post({
      url: '/branches/copy-menu',
      data: {
        fromBranchId: fromBranchId,
        toBranchId: toBranchId,
        adjustmentType: adjustmentType,
        adjustmentValue: adjustmentValue,
      },
    });
  },

  /**
   * Cập nhật giá món cho chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {string} menuItemId - ID món ăn
   * @param {number|null} priceOverride - Giá mới (hoặc null để dùng giá gốc)
   * @returns {Promise<Object>} Kết quả cập nhật với thông tin menu item
   */
  updateMenuItemPrice: async (branchId, menuItemId, priceOverride) => {
    return await apiClient.put({
      url: `/branches/${branchId}/menu-items/${menuItemId}/price`,
      data: {
        priceOverride: priceOverride,
      },
    });
  },

  /**
   * Cập nhật số dư tiền mặt của chi nhánh (Admin/Manager)
   * @param {string} branchId - ID chi nhánh
   * @param {Object} dto - Dữ liệu cập nhật
   * @param {number} dto.current_cash_balance - Số dư tiền mặt mới
   * @param {string} userId - ID người dùng hiện tại
   * @returns {Promise<Object>} Thông tin chi nhánh sau khi cập nhật
   */
  updateCashBalance: async (branchId, dto, userId) => {
    return await apiClient.patch({
      url: `/branches/${branchId}/cash-balance`,
      data: {
        ...dto,
      },
    });
  },
};
